/**
 * Sentry Configuration - Error Tracking and Monitoring
 * 
 * Environment variables required:
 * - VITE_SENTRY_DSN: Sentry project DSN (public)
 * - VITE_SENTRY_ENVIRONMENT: Environment name (development, staging, production)
 * - VITE_APP_VERSION: Application version for release tracking
 */

import * as Sentry from '@sentry/react'

export interface SentryConfig {
  dsn: string
  environment: string
  tracesSampleRate: number
  replaysSessionSampleRate: number
  replaysOnErrorSampleRate: number
  enabled: boolean
}

/**
 * Get Sentry configuration from environment variables
 */
export function getSentryConfig(): SentryConfig {
  const dsn = import.meta.env.VITE_SENTRY_DSN || ''
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE || 'development'
  const isDevelopment = environment === 'development'

  return {
    dsn,
    environment,
    // Sample rate for performance monitoring (0.0 - 1.0)
    // Lower in production to reduce costs
    tracesSampleRate: isDevelopment ? 1.0 : 0.1,
    // Session replay sample rate for normal sessions
    replaysSessionSampleRate: isDevelopment ? 1.0 : 0.1,
    // Session replay sample rate for sessions with errors (always capture errors)
    replaysOnErrorSampleRate: 1.0,
    // Disable Sentry if no DSN is provided
    enabled: !!dsn,
  }
}

/**
 * Initialize Sentry error tracking
 * Call this once at app startup (in main.tsx)
 */
export function initSentry() {
  const config = getSentryConfig()

  if (!config.enabled) {
    console.warn('Sentry DSN not configured. Error tracking disabled.')
    return
  }

  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,
    release: import.meta.env.VITE_APP_VERSION || 'unknown',
    
    // Performance monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    
    tracesSampleRate: config.tracesSampleRate,
    
    // Session Replay
    replaysSessionSampleRate: config.replaysSessionSampleRate,
    replaysOnErrorSampleRate: config.replaysOnErrorSampleRate,
    
    // Filter out sensitive data
    beforeSend(event, _hint) {
      // Don't send events if user hasn't connected wallet (likely just browsing)
      if (event.user && !event.user.id) {
        return null
      }
      
      // Remove sensitive request data
      if (event.request) {
        delete event.request.cookies
        delete event.request.headers
      }
      
      return event
    },
    
    // Ignore common non-critical errors
    ignoreErrors: [
      // Browser extension errors
      'top.GLOBALS',
      'chrome-extension://',
      'moz-extension://',
      // Network errors (often expected)
      'Network request failed',
      'NetworkError',
      'Failed to fetch',
      // WalletConnect/Web3 user cancellations (expected behavior)
      'User rejected',
      'User denied',
      'User cancelled',
      'ACTION_REJECTED',
    ],
  })

  console.log(`✅ Sentry initialized (${config.environment})`)
}

/**
 * Set user context for error tracking
 * Call this when user connects wallet
 */
export function setSentryUser(address: string, chainId?: number) {
  if (!getSentryConfig().enabled) return

  Sentry.setUser({
    id: address,
    username: address,
    chainId: chainId?.toString(),
  })
}

/**
 * Clear user context
 * Call this when user disconnects wallet
 */
export function clearSentryUser() {
  if (!getSentryConfig().enabled) return
  
  Sentry.setUser(null)
}

/**
 * Manually capture an exception
 */
export function captureException(error: Error, context?: Record<string, any>) {
  if (!getSentryConfig().enabled) {
    console.error('Sentry Error:', error, context)
    return
  }

  Sentry.captureException(error, {
    extra: context,
  })
}

/**
 * Manually capture a message
 */
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info', context?: Record<string, any>) {
  if (!getSentryConfig().enabled) {
    console.log(`Sentry Message [${level}]:`, message, context)
    return
  }

  Sentry.captureMessage(message, {
    level,
    extra: context,
  })
}

/**
 * Add breadcrumb for user actions
 */
export function addBreadcrumb(message: string, category: string, data?: Record<string, any>) {
  if (!getSentryConfig().enabled) return

  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  })
}

/**
 * Set custom context data
 */
export function setSentryContext(name: string, context: Record<string, any>) {
  if (!getSentryConfig().enabled) return

  Sentry.setContext(name, context)
}

/**
 * Add tag for filtering/grouping errors
 */
export function setSentryTag(key: string, value: string) {
  if (!getSentryConfig().enabled) return

  Sentry.setTag(key, value)
}
